import {
  AnalyticsTrackExternalLinkLaunchHandler,
  AppErrorType,
  MayBeNull,
  NoCodeAppType,
  TriggerErrorHandler,
  SourceLocations,
} from '@wpp-open/core'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useFetchAppInstanceApi } from 'api/apps/queryFetchers/useFetchAppInstanceApi'
import { useFetchAppInstancesApi } from 'api/apps/queryFetchers/useFetchAppInstancesApi'
import { RenderErrorType } from 'components/renderError'
import { useDispatchRenderError } from 'components/renderError/utils'
import { ApiQueryKeys } from 'constants/apiQueryKeys'
import { Delay } from 'constants/delay'
import { PageSize } from 'constants/pageSize'
import { useStableCallback } from 'hooks/useStableCallback'
import { useCurrentTenantData } from 'providers/currentTenantData/CurrentTenantDataContext'
import { queryClient } from 'providers/osQueryClient/utils'
import { useOsState } from 'providers/osState/OsStateProvider'
import { useOtherTenantsAndUserData } from 'providers/otherTenantsAndUserData/OtherTenantsAndUserDataContext'
import { useToast } from 'providers/toast/ToastProvider'
import { SystemAppCode } from 'types/apps/microApps'
import { AppLaunchData } from 'types/osState/appLaunchData'
import { trackAppOpenAnalytics } from 'utils/analytics'
import { isUUID } from 'utils/common'
import { simulateLinkClick } from 'utils/links'
import { getAppInstanceUrl } from 'utils/navigation'
import { isWorkspaceIdValid, resolveHierarchyPath } from 'utils/workspace'

export type OpenHandler = (
  appInstanceId: string,
  params?: {
    analytics?: {
      launchedFrom: string
    }
    target?: string
  },
  subPath?: string,
) => void

export const useOpenApp = (): OpenHandler => {
  const navigate = useNavigate()
  const { enqueueToast } = useToast()
  const { t } = useTranslation()

  // get the current workspace id from the app that is currently running
  const { appData, setAppLaunchData } = useOsState()
  const { activeWorkspaceId } = appData
  // get the current project from the currently running app
  const projectId = appData.project?.id
  // get root element for the navigation
  const {
    navigationTree: { rootId },
  } = useCurrentTenantData()
  const handleFetchAppInstances = useFetchAppInstancesApi()
  const handleFetchAppInstance = useFetchAppInstanceApi()

  // function that handles app launch by name if no uuid was provided
  const launchAppByName = async (appName: string, params: Omit<AppLaunchData, 'appInstanceId'>, subPath?: string) => {
    const {
      data: { data: appInstances },
    } = await handleFetchAppInstances({
      // filter app instances by project, workspace or rootNav element so that we limit the number of app instances we receive
      assignmentId: projectId || activeWorkspaceId || rootId,
      itemsPerPage: PageSize.All,
    })

    // search the app by name
    const app = appInstances.find(app => app.name === appName)

    // if no app exists with that name, display an error
    if (!app) {
      enqueueToast({
        duration: Delay.ToastLong,
        message: t('os.legacy_errors.open_app_not_found'),
        type: 'error',
      })
      console.error(`App with name "${appName}" is not found on the same level`)

      return
    }

    const appUrl = `${getAppInstanceUrl({
      id: app.id,
      osRoute: app.osRoute || app.devhubMetadata.version.routePath || undefined,
    })}${subPath || ''}`

    if (params?.target === '_blank') {
      simulateLinkClick({ href: appUrl, target: params.target })

      return
    }

    // this is used by analytics. Might have other uses in the future
    setAppLaunchData({
      appInstanceId: app.id,
      ...params,
    })
    navigate(appUrl)
  }

  const launchAppByInstanceId = async (
    appInstanceId: string,
    params: Omit<AppLaunchData, 'appInstanceId'>,
    subPath?: string,
  ) => {
    try {
      // we need the app instance in torder to get the osRoute so that we redirect to a valid APP URL and the user
      // doesn't get redirected
      const { data: appInstance } = await handleFetchAppInstance({ id: appInstanceId })

      const appUrl = `${getAppInstanceUrl({
        id: appInstanceId,
        osRoute: appInstance.osRoute || appInstance.devhubMetadata.version.routePath || undefined,
      })}${subPath || ''}`
      if (params?.target === '_blank') {
        simulateLinkClick({ href: appUrl, target: params.target })

        return
      }

      // this is used by analytics. Might have other uses in the future
      setAppLaunchData({
        appInstanceId,
        ...params,
      })
      await queryClient.resetQueries({ queryKey: [ApiQueryKeys.APP_INSTANCE, { id: appInstanceId }] })
      navigate(appUrl)
    } catch (e) {
      enqueueToast({
        duration: Delay.ToastLong,
        message: t('os.legacy_errors.open_app_not_found'),
        type: 'error',
      })
      console.error(`App with id "${appInstanceId}" is not found`)
    }
  }

  /**
   * @appIdentifier - string identifier for an app. This should be an appInstanceId but, the app that launches another
   *                  app doesn't have access to an app instance id but to a name. In order to solve this issue, the app
   *                   identifier can also be an app name woth the caveat that multiple apps, with the same name can
   *                   reside on the same assignment
   * @params - used by analytics to figure out who launched an app. This can have other uses in the future
   * @subPath - string, that should be appended to the app's URL
   */
  return useStableCallback(async (appIdentifier, params, subPath?: string) => {
    if (!appIdentifier) {
      throw new Error('OpenAppHandler: appInstanceId is empty')
    }

    let source: MayBeNull<SourceLocations> = null

    if (appData.app?.stableId === SystemAppCode.Orchestration) {
      source = SourceLocations.Orchestration
    }

    // if the identifier doesn't look like a UUID, search the app by name
    if (!isUUID(appIdentifier)) {
      return launchAppByName(
        appIdentifier,
        {
          source,
          launchedFrom: params?.analytics?.launchedFrom || '',
          target: params?.target,
        },
        subPath,
      )
    }

    launchAppByInstanceId(
      appIdentifier,
      {
        source,
        launchedFrom: params?.analytics?.launchedFrom || '',
        target: params?.target,
      },
      subPath,
    )
  })
}

const mapAppErrorType = (errorType: AppErrorType): RenderErrorType => {
  switch (errorType) {
    case AppErrorType.ForbiddenPage:
      return RenderErrorType.ForbiddenPage
    case AppErrorType.CriticalError:
      return RenderErrorType.CriticalError
    default:
      throw new Error('Unsupported AppErrorType')
  }
}

export const useTriggerError = (): TriggerErrorHandler => {
  const dispatchError = useDispatchRenderError()

  return useStableCallback(async (errorType: AppErrorType) => {
    dispatchError(mapAppErrorType(errorType))
  })
}

export const useTrackExternalLinkLaunch = (): AnalyticsTrackExternalLinkLaunchHandler => {
  const { navigationTreeWithHiddenLevel, currentTenant } = useCurrentTenantData()
  const { userDetails } = useOtherTenantsAndUserData()
  const { appData } = useOsState()

  return useStableCallback(
    async ({
      hierarchyNodeId,
      launchedFrom,
      productName,
      productOwnerEmail,
      productCommercialModel,
      productCategory,
      productSubcategory,
      appId,
      appInstanceId,
      appInstanceName,
    }) => {
      let source: MayBeNull<SourceLocations> = null

      if (appData.app?.stableId === SystemAppCode.Orchestration) {
        source = SourceLocations.Orchestration
      }

      const isValidHierarchyId = isWorkspaceIdValid({
        workspaceAzId: hierarchyNodeId,
        navigationTree: navigationTreeWithHiddenLevel,
      })

      const hierarchyWithHiddenLevel = resolveHierarchyPath({
        validHierarchyId: isValidHierarchyId ? hierarchyNodeId : null,
        tree: navigationTreeWithHiddenLevel,
      })

      trackAppOpenAnalytics({
        source,
        userDetails,
        productType: NoCodeAppType.ExternalLink,
        launchedFrom,
        productName,
        productOwnerEmail,
        productCommercialModel,
        productCategory,
        productSubcategory,
        appId,
        appInstanceId,
        appInstanceName,
        navigationTreeWithHiddenLevel,
        activeHierarchyWithHiddenLevel: hierarchyWithHiddenLevel,
        tenantName: currentTenant.name,
      })
    },
  )
}
